// /home/coder/project/frontend/src/pages/qaqc/inspections/QaqcInspRecordEdit.tsx
import React, { useState, useMemo, useEffect } from 'react';
import { Form, Input, Spin, Row, Col, message, Card, Select, DatePicker, Typography, Space, Divider, Checkbox, Radio, Result } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { SaveOutlined, AuditOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import {
  useGetProjSystems,
  useGetPersonnel,
} from '../../../hooks/genericHooks';
import { fetchSqlData, updateData } from '../../../services/api';
import type {
  VwQaqcInspRecord,
  VwQaqcInspFormDetails,
  QaqcInspForm, // Target interface for saving (update payload)
  ProjSystems,
  VwPersonnel,
} from '../../../types';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const QaqcInspRecordEdit: React.FC = () => {
  const { recordId } = useParams<{ recordId: string }>();
  const numericRecordId = recordId ? parseInt(recordId, 10) : undefined;

  const [form] = Form.useForm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [checklistItemsDefinition, setChecklistItemsDefinition] = useState<VwQaqcInspFormDetails[]>([]);
  const [currentFormDesignId, setCurrentFormDesignId] = useState<number | null>(null);
  const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false);
  const [selectAllIndeterminate, setSelectAllIndeterminate] = useState<boolean>(false);

  const {
    data: inspectionRecord,
    isLoading: isLoadingRecord,
    isError: isErrorRecord,
    error: errorRecordMsg,
  } = useQuery<VwQaqcInspRecord, Error>({
    queryKey: ['qaqcInspRecordDetail', numericRecordId],
    queryFn: async () => {
      if (!numericRecordId) throw new Error("No valid record ID provided");
      const results = await fetchSqlData('vw_qaqc_insp_records', 'record_id', numericRecordId);
      if (!results || results.length === 0) {
        throw new Error(`QA/QC Inspection Record with ID ${numericRecordId} not found.`);
      }
      return results[0];
    },
    enabled: !!numericRecordId,
  });

  const formDesignIdFromRecord = inspectionRecord?.form_id;
  const {
    data: fetchedChecklistItemsDefinition,
    isLoading: isLoadingChecklistDefinition,
    isError: isErrorChecklistDefinition,
    error: errorChecklistDefinitionMsg,
  } = useQuery<VwQaqcInspFormDetails[], Error>({
    queryKey: ['qaqcFormChecklistItemsForRecordEdit', formDesignIdFromRecord],
    queryFn: async () => {
      if (!formDesignIdFromRecord) return [];
      const data = await fetchSqlData('vw_qaqc_insp_formdetails', 'design_id', formDesignIdFromRecord);
      return (data as VwQaqcInspFormDetails[]).sort((a, b) => a.field_id - b.field_id);
    },
    enabled: !!formDesignIdFromRecord,
  });

  const { data: projSystemsData, isLoading: isLoadingProjSystems } = useGetProjSystems();
  const projSystems: ProjSystems[] = projSystemsData?.data || [];
  const { data: personnelData, isLoading: isLoadingPersonnel } = useGetPersonnel();
  const personnelList: VwPersonnel[] = personnelData?.data || [];

  useEffect(() => {
    if (inspectionRecord && fetchedChecklistItemsDefinition) {
      setCurrentFormDesignId(inspectionRecord.form_id);
      setChecklistItemsDefinition(fetchedChecklistItemsDefinition);

      form.setFieldsValue({
        sys_id: inspectionRecord.sys_id,
        insp_date: inspectionRecord.insp_date ? dayjs(inspectionRecord.insp_date) : null,
        insp_place: inspectionRecord.insp_place,
        inspector_id: inspectionRecord.inspector_id,
        witness1: inspectionRecord.witness1_id,
        witness2: inspectionRecord.witness2_id,
        form_notes: inspectionRecord.form_notes,
        overall_form_result: inspectionRecord.form_result ? 'Pass' : 'Fail',
      });

      const checklistValues: any = {};
      fetchedChecklistItemsDefinition.forEach((item, index) => {
        checklistValues[`item_status_${item.field_id}`] = inspectionRecord.form_field_values?.[index] ?? false;
        checklistValues[`item_remarks_${item.field_id}`] = inspectionRecord.form_field_remarks?.[index] ?? '';
      });
      form.setFieldsValue(checklistValues);
    }
  }, [inspectionRecord, fetchedChecklistItemsDefinition, form]);

  useEffect(() => {
    if (!checklistItemsDefinition || checklistItemsDefinition.length === 0) {
      setSelectAllChecked(false);
      setSelectAllIndeterminate(false);
      return;
    }
    const formValues = form.getFieldsValue();
    let checkedCount = 0;
    checklistItemsDefinition.forEach(item => {
      if (formValues[`item_status_${item.field_id}`] === true) {
        checkedCount++;
      }
    });

    if (checkedCount === 0) {
      setSelectAllChecked(false);
      setSelectAllIndeterminate(false);
    } else if (checkedCount === checklistItemsDefinition.length) {
      setSelectAllChecked(true);
      setSelectAllIndeterminate(false);
    } else {
      setSelectAllChecked(false);
      setSelectAllIndeterminate(true);
    }
  }, [checklistItemsDefinition, form, form.getFieldsValue(checklistItemsDefinition.map(item => `item_status_${item.field_id}`))]);

  const updateRecordMutation = useMutation<QaqcInspForm, Error, Partial<QaqcInspForm>>({
    mutationFn: (payload) => {
      if (!numericRecordId) throw new Error("Record ID is missing for update.");
      return updateData('qaqc_insp_forms', numericRecordId, payload);
    },
    onSuccess: (data) => {
      message.success(`QA/QC Inspection Record (ID: ${data.id}) updated successfully!`);
      queryClient.invalidateQueries({ queryKey: ['vwQaqcInspRecords'] });
      queryClient.invalidateQueries({ queryKey: ['qaqcInspRecordDetail', numericRecordId] });
      navigate(-1);
    },
    onError: (error: any) => {
      console.error('Error updating QA/QC inspection record:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to update record.';
      message.error(`Error: ${errorMsg}`);
    },
  });

  const onFinish = (values: any) => {
    if (!currentFormDesignId) {
      message.error('Form Design ID is missing. Cannot save.');
      return;
    }

    const formFieldValues: boolean[] = [];
    const formFieldRemarks: (string | null)[] = [];

    checklistItemsDefinition.forEach(item => {
      const statusIsTrue = values[`item_status_${item.field_id}`];
      const remarks = values[`item_remarks_${item.field_id}`] || null;
      formFieldValues.push(!!statusIsTrue);
      formFieldRemarks.push(remarks);
    });

    const payload: Partial<QaqcInspForm> = {
      form_id: currentFormDesignId,
      sys_id: values.sys_id,
      insp_date: values.insp_date ? values.insp_date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      insp_place: values.insp_place || null,
      inspector_id: values.inspector_id,
      form_field_values: formFieldValues.length > 0 ? formFieldValues : null,
      form_field_remarks: formFieldRemarks.length > 0 ? formFieldRemarks : null,
      form_result: values.overall_form_result === 'Pass',
      form_notes: values.form_notes || null,
      witness1: values.witness1 || null,
      witness2: values.witness2 || null,
    };
    updateRecordMutation.mutate(payload);
  };

  const handleSelectAllChange = (e: CheckboxChangeEvent) => {
    const isChecked = e.target.checked;
    setSelectAllChecked(isChecked);
    setSelectAllIndeterminate(false);

    if (!checklistItemsDefinition) return;
    const updatedValues: { [key: string]: boolean } = {};
    checklistItemsDefinition.forEach(item => {
      updatedValues[`item_status_${item.field_id}`] = isChecked;
    });
    form.setFieldsValue(updatedValues);
  };

  const overallLoading = isLoadingRecord || isLoadingChecklistDefinition || isLoadingProjSystems || isLoadingPersonnel || updateRecordMutation.isPending;

  if (!numericRecordId && !isLoadingRecord) {
    return <Result status="error" title="Invalid Record ID" subTitle="No valid numeric ID provided in the URL." extra={<Button type="primary" onClick={() => navigate('/qaqc/inspections/records')}>Go Back to List</Button>} />;
  }

  if (isLoadingRecord || (formDesignIdFromRecord && isLoadingChecklistDefinition && !inspectionRecord)) {
     return (
      <Card title={<Space><AuditOutlined /><Title level={4} style={{ margin: 0 }}>Edit QA/QC Inspection Record</Title></Space>}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <Spin size="large" tip="Loading inspection data..." />
        </div>
      </Card>
    );
  }

  if (isErrorRecord) {
    return <Result status="error" title="Error Loading Record" subTitle={errorRecordMsg?.message || 'Failed to load inspection record.'} extra={<Button type="primary" onClick={() => navigate('/qaqc/inspections/records')}>Go Back to List</Button>} />;
  }
  if (isErrorChecklistDefinition) {
     return <Result status="error" title="Error Loading Form Details" subTitle={errorChecklistDefinitionMsg?.message || 'Failed to load form checklist items.'} extra={<Button type="primary" onClick={() => navigate(-1)}>Go Back</Button>} />;
  }
  if (!inspectionRecord) {
    return <Result status="warning" title="Record Not Found" subTitle={`QA/QC Inspection Record with ID ${numericRecordId} could not be found.`} extra={<Button type="primary" onClick={() => navigate('/qaqc/inspections/records')}>Go Back to List</Button>} />;
  }

  return (
    <Card
      title={
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} type="text" />
          <AuditOutlined />
          <Title level={4} style={{ margin: 0 }}>Edit QA/QC Inspection Record (ID: {numericRecordId})</Title>
        </Space>
      }
    >
      <Spin spinning={overallLoading} tip={updateRecordMutation.isPending ? "Saving Changes..." : "Loading data..."}>
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          onFinish={onFinish}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Inspection Form">
                <Text strong>{inspectionRecord.form_design_name} ({inspectionRecord.form_design_num})</Text>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="sys_id" label="Project System ID" rules={[{ required: true, message: 'Please select a project system!' }]}>
                <Select placeholder="Select Project System" loading={isLoadingProjSystems} showSearch filterOption={(input, option) => (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase()) || (option?.value as string ?? '').toLowerCase().includes(input.toLowerCase())}>
                  {projSystems.map(system => <Option key={system.sys_id} value={system.sys_id}>{`${system.sys_name} (${system.sys_id})`}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="insp_date" label="Inspection Date" rules={[{ required: true, message: 'Please select inspection date!' }]}>
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="insp_place" label="Location / Area">
                <Input placeholder="Enter Inspection Place" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="inspector_id" label="Inspector" rules={[{ required: true, message: 'Please select an inspector!' }]}>
                <Select placeholder="Select Inspector" loading={isLoadingPersonnel} showSearch filterOption={(input, option) => (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase())}>
                  {personnelList.map(p => <Option key={p.id} value={p.id}>{`${p.first_name} ${p.last_name}`}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="witness1" label="Witness 1">
                <Select placeholder="Select Witness 1" loading={isLoadingPersonnel} allowClear showSearch filterOption={(input, option) => (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase())}>
                  {personnelList.map(p => <Option key={p.id} value={p.id}>{`${p.first_name} ${p.last_name}`}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="witness2" label="Witness 2">
                <Select placeholder="Select Witness 2" loading={isLoadingPersonnel} allowClear showSearch filterOption={(input, option) => (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase())}>
                  {personnelList.map(p => <Option key={p.id} value={p.id}>{`${p.first_name} ${p.last_name}`}</Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {currentFormDesignId && checklistItemsDefinition.length > 0 && (
            <Row align="middle" style={{ marginTop: 16, marginBottom: 12 }}>
              <Col flex="auto"><Divider orientation="left" plain style={{ margin: 0 }}>Check Items</Divider></Col>
              <Col style={{ paddingLeft: 16 }}>
                <Checkbox checked={selectAllChecked} indeterminate={selectAllIndeterminate} onChange={handleSelectAllChange}>Select All Pass</Checkbox>
              </Col>
            </Row>
          )}

          {isLoadingChecklistDefinition && currentFormDesignId && <Spin tip="Loading checklist items..." style={{ display: 'block', marginTop: 16 }} />}

          {currentFormDesignId && !isLoadingChecklistDefinition && checklistItemsDefinition.length > 0 && (
            checklistItemsDefinition.map((item, index) => (
              <Row key={item.field_id} gutter={16} align="middle" style={{ marginBottom: '8px' }}>
                <Col xs={24} sm={24} md={12}><Text>{index + 1}. {item.check_item}</Text></Col>
                <Col xs={12} sm={6} md={4}>
                  <Form.Item name={`item_status_${item.field_id}`} valuePropName="checked" noStyle>
                    <Checkbox>Pass</Checkbox>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={18} md={8}>
                  <Form.Item name={`item_remarks_${item.field_id}`} noStyle>
                    <TextArea rows={1} placeholder="Remarks" />
                  </Form.Item>
                </Col>
              </Row>
            ))
          )}
          {!isLoadingChecklistDefinition && currentFormDesignId && checklistItemsDefinition.length === 0 && (
            <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>Selected form design does not have any checklist items.</Text>
          )}

          <Divider style={{ marginTop: 24, marginBottom: 12 }}>Summary</Divider>
          <Row gutter={16}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item name="form_notes" label="Overall Notes" labelCol={{sm: 24, md: 5}} wrapperCol={{sm:24, md:19}}>
                <TextArea rows={3} placeholder="Enter any overall notes for the inspection" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Form.Item name="overall_form_result" label="Overall Result" rules={[{ required: true, message: 'Please select the overall result!' }]} labelCol={{sm: 24, md: 5}} wrapperCol={{sm:24, md:19}}>
                <Radio.Group><Radio value="Pass">Pass</Radio><Radio value="Fail">Fail</Radio></Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item wrapperCol={{ offset: 8, span: 16 }} style={{ textAlign: 'right', marginTop: '24px' }}>
            <Space>
              <Button onClick={() => navigate(-1)}>Cancel</Button>
              <Button action="edit" type="primary" htmlType="submit" icon={<SaveOutlined />} loading={updateRecordMutation.isPending} disabled={!currentFormDesignId || isLoadingRecord || isLoadingChecklistDefinition || updateRecordMutation.isPending}>
                Save Changes
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </Card>
  );
};

export default QaqcInspRecordEdit;